import { AfterViewInit, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as MomentLib from 'moment';
import { GenericModel, MapModel } from 'src/app/app.model';
import { GoogleMapService } from './google-map.service';

declare var google: any;

@Component({
  selector: 'google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss']
})
export class GoogleMapComponent implements AfterViewInit, OnChanges {
  zones: Zone[] = [];
  @Input() mapModel: MapModel;
  @Input() dataviewData: GenericModel;

  currentMapModel: MapModel;
  googleMap;

  private nzBounds = false;
  private bounds: any = null;

  private markers: any[] = [];
  private circles: any[] = [];
  private stops: any[] = [];
  private polylines: any[] = [];

  // Google maps zoom level
  public zoom = 9;

  constructor(private mapService: GoogleMapService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.googleMap) {
      if (this.currentMapModel) {
        this.deleteMarkers();
      }
      this.currentMapModel = this.mapModel;
      this.setBusInformation(this.currentMapModel, this.googleMap);
      this.showMarkers(this.googleMap);
      this.setBounds(this.googleMap);
    }
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  initMap() {
    const params = {
      width: '100%',
      height: '100%'
    };
    if (typeof window['functionFromExternalScript'] === 'function') {
      window['functionFromExternalScript'](params);
    }

    const center = { lat: -41.034, lng: 175.519 };
    // The map, centered at center given
    if (!this.googleMap) {
      this.mapService
        .getMap(document.getElementById('google-map'), { zoom: this.zoom, center: center, styles: this.styles, mapTypeControl: false })
        .then((googleMapObj) => {
          this.googleMap = googleMapObj;
          this.setZones(this.googleMap);
        });
    }
  }

  restrictBoundsToNZ(NZ: boolean) {
    this.nzBounds = NZ;
  }

  withinNZ(lat: any, lng: any): boolean {
    // var NEW_ZEALAND_BOUNDS = {
    //     north: -34.36,
    //     south: -47.35,
    //     west: 166.28,
    //     east: -175.81,
    //   };
    const ne = new google.maps.LatLng(-34.36, -175.81);
    const sw = new google.maps.LatLng(-47.35, 166.28);
    const bounds = new google.maps.LatLngBounds(sw, ne);
    const position = new google.maps.LatLng(lat, lng);

    return bounds.contains(position);
  }

  setBusInformation(currentMapModel: MapModel, map) {
    this.setMarkers(currentMapModel, map);
    this.setCircles(currentMapModel);
    this.setStops(currentMapModel, map);
    this.setPolyLines(currentMapModel);
  }

  showMarkers(map) {
    this.setMapOnAll(map);
  }

  clearMarkers() {
    this.setMapOnAll(null);
  }

  deleteMarkers() {
    this.clearMarkers();
    this.markers = [];
    this.circles = [];
    this.stops = [];
    this.polylines = [];
  }

  setZones(map) {
    for (let p = 0; p < this.zones.length; p++) {
      const polygon = new google.maps.Polygon({
        // paths: this.zones[p].coords,
        paths: this.zones[p],
        fillColor: p % 2 == 0 ? '#0a5d66' : '#65CBC9',
        strokeWeight: 0,
        fillOpacity: p % 2 == 0 ? 0.29 : 0.45,
        map: map
      });
    }
  }

  setMarkers(currentModel: MapModel, map) {
    if (currentModel.markers) {
      currentModel.markers.forEach((m) => {
        // refrence to this is lost after load?
        const dataviewDataReference = this.dataviewData;
        let time;
        if (m.time) {
          time = m.time;
        } else {
          if (m.cpTimeNz) {
            const cpTimeNzMoment = MomentLib(m.cpTimeNz, 'YYYY-MM-DDTHH:mm:ss');
            time = cpTimeNzMoment.format('HH:mm');
          }
        }
        const infowindow = new google.maps.InfoWindow({
          content: 'Stop: ' + (m.stop ?? 'n/a') + ' - Time: ' + (time ?? 'n/a')
        });

        let lat;
        let lng;
        // tslint:disable-next-line: triple-equals
        if (m.lat && m.lat != 0 && m.lng && m.lng != 0) {
          lat = m.lat;
          lng = m.lng;
        } else {
          lat = m.cplat;
          lng = m.cplng;
        }

        let zIndex = 10;
        if (m.isFirstLastHb) {
          zIndex = 15;
        }
        if (!this.nzBounds || this.withinNZ(lat, lng)) {
          const marker = new google.maps.Marker({
            position: new google.maps.LatLng(lat, lng),
            icon: m.iconPath,
            zIndex: zIndex
          });

          marker.addListener('click', () => {
            infowindow.open(map, marker);
            if (dataviewDataReference) {
              dataviewDataReference['google-maps-selected-heartbeat_time'] = m.time;
            }
          });

          this.markers.push(marker);
        }
      });
    }
  }

  setCircles(currentModel: MapModel) {
    if (currentModel.circles) {
      currentModel.circles.forEach((c) => {
        const circle = new google.maps.Circle({
          center: { lat: c.cplat, lng: c.cplng },
          radius: 16,
          strokeWeight: 0,
          fillColor: c.color
        });
        this.circles.push(circle);
      });
    }
  }

  setStops(currentModel: MapModel, map) {
    if (currentModel.stops) {
      currentModel.stops.forEach((s) => {
        const infowindow = new google.maps.InfoWindow({
          content: 'Stop: ' + s.name + ' - StopId: ' + s.ksccStopId + ' - Time: ' + s.arrivalTime
        });

        const stop = new google.maps.Marker({
          position: new google.maps.LatLng(s.gtfsLat, s.gtfsLng),
          icon: s.iconPath,
          zIndex: 2
        });

        stop.addListener('click', () => {
          infowindow.open(map, stop);
        });

        this.stops.push(stop);
      });
    }
  }

  setPolyLines(currentModel: MapModel) {
    if (currentModel.scheduledLine.scheduledCoords) {
      const polyline = new google.maps.Polyline({
        path: currentModel.scheduledLine.scheduledCoords,
        strokeColor: currentModel.scheduledLine.strokeColor,
        strokeWeight: currentModel.scheduledLine.strokeWeight,
        zIndex: 1
      });
      this.polylines.push(polyline);
    }
  }

  setMapOnAll(googleMap) {
    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].setMap(googleMap);
    }

    for (let i = 0; i < this.stops.length; i++) {
      this.stops[i].setMap(googleMap);
    }

    for (let i = 0; i < this.polylines.length; i++) {
      this.polylines[i].setMap(googleMap);
    }

    for (let i = 0; i < this.circles.length; i++) {
      this.circles[i].setMap(googleMap);
    }
  }

  setBounds(googleMap) {
    this.bounds = this.getBoundsFindMyBus();
    googleMap.fitBounds(this.bounds);
  }

  getBoundsFindMyBus() {
    let boundsTemp = new google.maps.LatLngBounds();

    if (this.markers && this.markers.length > 0) {
      const lat = this.markers[0].position.lat();
      const lng = this.markers[0].position.lng();
      if (lat && lng) {
        boundsTemp.extend(new google.maps.LatLng(lat - 0.001, lng - 0.001));
        boundsTemp.extend(new google.maps.LatLng(lat + 0.001, lng + 0.001));
      }
    }

    return boundsTemp;
  }

  // Google map styling
  styles: any[] = [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#f5f5f5'
        }
      ]
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161'
        }
      ]
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f5f5'
        }
      ]
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd'
        }
      ]
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee'
        }
      ]
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5'
        }
      ]
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e'
        }
      ]
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff'
        }
      ]
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575'
        }
      ]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dadada'
        }
      ]
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161'
        }
      ]
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e'
        }
      ]
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5'
        }
      ]
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee'
        }
      ]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#c9c9c9'
        }
      ]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e'
        }
      ]
    }
  ];
}
